import React, { useState } from "react";
import { twMerge } from "tailwind-merge";
import {
  HiCheck,
  HiOutlineArrowRight,
  HiOutlineSparkles,
} from "react-icons/hi";
import { CustomButton } from "./CustomButton";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";
import { useTrialEligibility } from "../../hooks/promoCode/trial";
import { TrialActivationModal } from "../molecules/TrialActivationModal";

export interface PremiumBenefit {
  id: string;
  text: string;
  icon?: React.ComponentType<any>;
}

export interface UpgradePromptContentProps {
  title: string;
  description: string;
  benefits?: PremiumBenefit[];
  icon?: React.ComponentType<any>;
  iconTheme?: "yellow" | "blue" | "purple" | "green";
  upgradeText: string;
  onUpgrade: () => void;
  onCancel?: () => void;
  className?: string;
  flat?: boolean;
}

export const UpgradePromptContent: React.FC<UpgradePromptContentProps> = ({
  title,
  description,
  benefits = [],
  icon: IconComponent = HiOutlineSparkles,
  upgradeText,
  onUpgrade,
  onCancel,
  className = "",
  flat = false,
}) => {
  const [t] = useTranslation();
  const { eligible } = useTrialEligibility();
  const [showTrial, setShowTrial] = useState(false);

  return (
    <>
      <div
        className={twMerge(
          !flat &&
            "bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-3 sm:p-6",
          className
        )}
      >
        <div className="flex items-start gap-2 sm:gap-4">
          <div className="hidden sm:flex flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full items-center justify-center">
            <IconComponent size={24} className="text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2 font-figtree">
              {title}
            </h3>

            <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed font-figtree">
              {description}
            </p>

            {benefits.length > 0 && (
              <div className="mb-3 sm:mb-4">
                <ul className="space-y-1.5 sm:space-y-2">
                  {benefits.map((benefit) => (
                    <li
                      key={benefit.id}
                      className="flex items-center text-xs sm:text-sm text-gray-600"
                    >
                      <div className="w-4 h-4 sm:w-5 sm:h-5 bg-green-500 rounded-full mr-2 sm:mr-3 flex-shrink-0 flex items-center justify-center">
                        <HiCheck
                          size={12}
                          className="sm:w-3.5 sm:h-3.5 text-white"
                        />
                      </div>
                      <span className="font-figtree">
                        {parse(benefit.text)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-3 flex flex-col gap-2 sm:mt-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
              {onCancel && (
                <CustomButton
                  variant="none"
                  onClick={onCancel}
                  className="order-last w-full shrink-0 whitespace-nowrap px-3 text-gray-600 hover:text-gray-800 sm:order-none sm:w-auto"
                  title={t("common.cancelText")}
                />
              )}
              {eligible && (
                <CustomButton
                  variant="primary"
                  onClick={() => setShowTrial(true)}
                  className="w-full shrink-0 whitespace-nowrap sm:w-auto"
                  title={t("dashboard.trialOffer.ctaShort")}
                />
              )}
              <CustomButton
                variant={eligible ? "secondary" : "primary"}
                onClick={onUpgrade}
                className="w-full shrink-0 whitespace-nowrap sm:w-auto"
                title={
                  <>
                    <span>{upgradeText}</span>
                    <HiOutlineArrowRight size={16} className="ml-1 shrink-0" />
                  </>
                }
              />
            </div>
          </div>
        </div>
      </div>
      <TrialActivationModal
        show={showTrial}
        onClose={() => setShowTrial(false)}
      />
    </>
  );
};

export default UpgradePromptContent;
